import React, { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Select, Table, Tooltip, Button, message, Dropdown, Menu, Modal, Radio } from 'antd';
import {
  TmsCarrierReceivablesProps,
  TmsCarrierReceivablesRecord,
  STATUS_TYPES,
} from 'src/components/pages/Receivables/Receivables.utils';
import {
  formatDateTime,
  formatNumber,
  capitalize,
  downloadFileData,
} from 'src/utils/utils';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import {
  ReloadOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  VerticalLeftOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import ReferenceDetailsPopover from './components/ReferenceDetailsPopover';
import CalculationDetailsDrawer from './components/CalculationDetailsDrawer';
import AddChargeDrawer from './components/AddChargeDrawer';
import ExcelUpload from 'src/components/common/ExcelUpload';
import { fetchPayoutData, setActiveViewType } from './Receivables.actionsCreator';
import { TmsCarrierReceivablesStyles } from './Invoices.styles';
import Filters, { FilterValues, BucketFilters } from './components/Filters';
import ConfirmActionModal from 'src/common/ConfirmActionModal';
import { getDefaultDateRange } from './components/BillingCyclePicker';
import { SET_RECEIVABLES_FILTERS, SET_RECEIVABLES_PAGINATION } from 'src/common/actions/constants';
import { downloadRequestRoute } from 'src/routing/routeConstants';
import { useGenericState } from 'src/library/UseGenericState';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  sendPayoutForReview,
  bulkSendPayoutForReview,
  downloadPayoutExcel,
  downloadSampleAdhocChargesExcel,
  uploadBulkAdhocCharges,
  processSingleAdhocCharge,
} from './Receivables.api';
import { GenericBulkUploadService } from 'src/components/GenericBulkUploadService';

interface PayoutRequestData {
  start_date?: string;
  end_date?: string;
  object_type: string;
  entity_type_list?: string[];
  entity_reference_number_list?: string[];
  invoice_status?: string;
  invoice_number_list?: string[];
  status_list?: string[];
  planning_hub_id_list?: string[];
  page_number?: number;
  limit?: number;
  date_type?: string;
}

interface CalculationsProps extends TmsCarrierReceivablesProps {
  activeTab: string;
}

interface CalculationsUIState {
  activeTabKey: string;
  selectedRowKeys: React.Key[];
  searchFilterQuery: string;
  searching: boolean;
  searchFilterId: string;
  selectedCalculation: any;
  detailsModalVisible: boolean;
  bucketFilters: BucketFilters;
  confirmActionModalVisible: boolean;
  addChargeDrawerVisible: boolean;
  showAddAdhocChargeModal: boolean;
  showBulkUploadAdhocChargeModal: boolean;
  excelData: any[];
  validationPopupVisible: boolean;
  validationErrors: any[];
  duplicateData: any;
  currentRequestBody: any;
}

const Calculations: React.FC<CalculationsProps> = ({ activeTab, ...props }) => {
  const { classes, tmsCarrierReceivables, setFilters: setReduxFilters, setPagination } = props;

  const { filters: reduxFilters, pagination } = tmsCarrierReceivables;

  const { t } = useTranslation();
  const history = useHistory();

  const [state, setState] = useGenericState<CalculationsUIState>({
    activeTabKey: '',
    selectedRowKeys: [],
    searchFilterQuery: '',
    searching: false,
    searchFilterId: '',
    selectedCalculation: null,
    detailsModalVisible: false,
    bucketFilters: {},
    confirmActionModalVisible: false,
    addChargeDrawerVisible: false,
    showAddAdhocChargeModal: false,
    showBulkUploadAdhocChargeModal: false,
    excelData: [],
    validationPopupVisible: false,
    validationErrors: [],
    duplicateData: null,
    currentRequestBody: null,
  });

  const { activeTabKey, selectedCalculation, detailsModalVisible } = state;

  const currentBucketColumns = tmsCarrierReceivables.payoutBucketColumns?.[activeTabKey] || [];
  const masterDataViews = tmsCarrierReceivables.views || [];
  const { activeViewType, isPayoutMasterDataLoaded } = tmsCarrierReceivables;
  const allowVendorsToAddAdhocCharge = tmsCarrierReceivables.allowVendorsToAddAdhocCharge || false;
  const enableGlobalAddChargeOption = tmsCarrierReceivables.enableGlobalAddChargeOption || false;
  const enableGlobalAddChargeSingleOption =
    tmsCarrierReceivables.enableGlobalAddChargeSingleOption || false;
  const billingCycleConfig = {
    type: tmsCarrierReceivables.billingCycleType,
    start: tmsCarrierReceivables.billingCycleStart,
  };
  const getDefaultFiltersForBucket = (): FilterValues => {
    const { startDate, endDate } = getDefaultDateRange(
      billingCycleConfig.type,
      billingCycleConfig.start,
    );

    return {
      dateRange: [startDate, endDate],
    };
  };

  useEffect(() => {
    const payoutBuckets = tmsCarrierReceivables?.payoutBuckets || {};
    const bucketKeys = Object.keys(payoutBuckets);
    if (bucketKeys.length > 0 && !bucketKeys.includes(activeTabKey)) {
      setState({ activeTabKey: bucketKeys[0] });
    }
  }, [tmsCarrierReceivables?.payoutBuckets]);

  useEffect(() => {
    if (!state.bucketFilters[activeTabKey]) {
      const defaultFilters = getDefaultFiltersForBucket();
      setState({
        bucketFilters: {
          ...state.bucketFilters,
          [activeTabKey]: defaultFilters,
        },
      });
    }
  }, [activeTabKey]);

  const getCurrentBucketFilters = () => {
    return state.bucketFilters[activeTabKey] || getDefaultFiltersForBucket();
  };

  const handleFiltersChange = (newFilters: FilterValues, bucket: string) => {
    if (moment(newFilters.dateRange[1]).diff(moment(newFilters.dateRange[0]), 'days') > 31) {
      message.error('Date range cannot be of more than 31 days');
      return;
    }

    setState({
      bucketFilters: {
        ...state.bucketFilters,
        [bucket]: newFilters,
      },
    });

    const requestFilters = {
      ...reduxFilters,
      entityType: newFilters.entityType,
      referenceNumber: newFilters.referenceNumber,
      invoiceNumber: newFilters.invoiceNumber,
      status: newFilters.status,
      hub: newFilters.hub,
    };

    setReduxFilters(requestFilters);
  };

  const loadPayoutData = useCallback(() => {
    const filters = getCurrentBucketFilters();
    const statusValue = filters?.status && filters.status !== 'all' ? filters.status : activeTabKey;
    const requestData: PayoutRequestData = {
      start_date: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
      end_date: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
      object_type: 'vendor',
      entity_type_list: filters.entityType ? [filters.entityType] : undefined,
      entity_reference_number_list: filters.referenceNumber ? [filters.referenceNumber] : undefined,
      status_list: statusValue ? [statusValue] : [],
      planning_hub_id_list: filters.hub ? [filters.hub] : [],
      page_number: pagination.pageNumber,
      limit: pagination.resultPerPage,
      date_type: 'payout_cycle',
      invoice_number_list: filters.invoiceNumber ? [filters.invoiceNumber] : undefined,
      ...(activeViewType ? { view_type: activeViewType } : {}),
    };
    props.fetchPayoutData(requestData);
  }, [
    state.bucketFilters,
    activeTabKey,
    activeViewType,
    pagination.pageNumber,
    pagination.resultPerPage,
  ]);

  useEffect(() => {
    if (!isPayoutMasterDataLoaded) {
      return;
    }
    if (state.bucketFilters?.[activeTabKey]?.dateRange?.length === 2) {
      loadPayoutData();
    }
  }, [
    isPayoutMasterDataLoaded,
    activeTab,
    activeTabKey,
    activeViewType,
    state.bucketFilters[activeTabKey]?.dateRange?.[0],
    state.bucketFilters[activeTabKey]?.dateRange?.[1],
    state.bucketFilters[activeTabKey]?.status,
    state.bucketFilters[activeTabKey]?.hub,
    state.bucketFilters[activeTabKey]?.entityType,
    state.bucketFilters[activeTabKey]?.referenceNumber,
    state.bucketFilters[activeTabKey]?.invoiceNumber,
    pagination.pageNumber,
    pagination.resultPerPage,
  ]);

  const handleDownloadPayoutExcel = async () => {
    const filters = getCurrentBucketFilters();
    const downloadStatusValue = filters.status && filters.status !== 'all' ? filters.status : activeTabKey;
    const requestData = {
      object_type: 'vendor',
      entity_type_list: filters.entityType ? [filters.entityType] : undefined,
      entity_reference_number_list: filters.referenceNumber ? [filters.referenceNumber] : undefined,
      status_list: downloadStatusValue ? [downloadStatusValue] : [],
      start_date: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
      end_date: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
      date_type: 'payout_cycle',
      planning_hub_id_list: filters.hub ? [filters.hub] : [],
    };

    const hideLoading = message.loading('Initiating download request...', 0);
    try {
      const response = await downloadPayoutExcel(requestData, activeViewType);
      if (response.isSuccess) {
        message.success('Download request submitted successfully');
        history.push(`${downloadRequestRoute}?dumpType=VENDOR_PAYOUT`);
      } else {
        message.error(response.errorMessage || 'Failed to submit download request');
      }
    } catch (error) {
      message.error('An error occurred while submitting download request');
    } finally {
      hideLoading();
    }
  };

  const renderActionBar = () => (
    <div className={classes.actionBar}>
      <Tooltip title="Refresh">
        <Button
          icon={<ReloadOutlined style={{ fontSize: '16px' }} />}
          onClick={loadPayoutData}
          className={classes.iconButton}
        />
      </Tooltip>
      <Tooltip title="Download">
        <Button
          icon={<DownloadOutlined style={{ fontSize: '16px' }} />}
          onClick={handleDownloadPayoutExcel}
          className={classes.iconButton}
        />
      </Tooltip>
      {activeTabKey === 'pending' && (
        <Button
          className={classes.sendForReviewButton}
          disabled={state.selectedRowKeys.length === 0}
          onClick={() => setState({ confirmActionModalVisible: true })}
        >
          Send for Review
        </Button>
      )}
      {activeTabKey === 'pending' && allowVendorsToAddAdhocCharge && enableGlobalAddChargeOption && (
        <Dropdown
          overlay={
            <Menu>
              {enableGlobalAddChargeSingleOption && (
                <Menu.Item
                  key="single"
                  onClick={() => setState({ showAddAdhocChargeModal: true })}
                  className={classes.menuItem}
                >
                  {t('Single')}
                </Menu.Item>
              )}
              <Tooltip
                title={!getCurrentBucketFilters().hub ? t('Hub selection is required') : ''}
                placement="right"
                overlayClassName="hub-selection-tooltip"
              >
                <div>
                  <Menu.Item
                    key="bulk"
                    onClick={() => {
                      if (getCurrentBucketFilters().hub) {
                        setState({ showBulkUploadAdhocChargeModal: true });
                      }
                    }}
                    disabled={!getCurrentBucketFilters().hub}
                    className={classes.menuItem}
                    style={{
                      cursor: !getCurrentBucketFilters().hub ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('Bulk')}
                  </Menu.Item>
                </div>
              </Tooltip>
            </Menu>
          }
          trigger={['click']}
        >
          <Button className={classes.addChargeButton}>{t('add_charge')}</Button>
        </Dropdown>
      )}
      <Button
        icon={
          <VerticalLeftOutlined style={{ fontSize: '10px', transform: 'rotate(180deg)' }} />
        }
        onClick={() => {
          setPagination({
            ...pagination,
            pageNumber: 1,
            nextOrPrev: 'first',
          });
        }}
        disabled={pagination.pageNumber === 1 || tmsCarrierReceivables.loader}
        className={classes.paginationButton}
      />
      <Button
        icon={<LeftOutlined style={{ fontSize: '10px' }} />}
        onClick={() => {
          setPagination({
            ...pagination,
            pageNumber: Math.max(1, pagination.pageNumber - 1),
            nextOrPrev: 'prev',
          });
        }}
        disabled={pagination.pageNumber === 1 || tmsCarrierReceivables.loader}
        className={classes.paginationButton}
      />
      <span className={classes.pageNumber}>{pagination.pageNumber}</span>
      <Button
        icon={<RightOutlined style={{ fontSize: '10px' }} />}
        onClick={() => {
          setPagination({
            ...pagination,
            pageNumber: pagination.pageNumber + 1,
            nextOrPrev: 'next',
          });
        }}
        disabled={!pagination.isNextPresent || tmsCarrierReceivables.loader}
        className={classes.paginationButton}
      />
      <Select
        className={classes.perPageSelect}
        onChange={(resultPerPage) => {
          setPagination({ ...pagination, resultPerPage, pageNumber: 1 });
        }}
        value={pagination.resultPerPage || 10}
      >
        <Select.Option value={10}>10 / Page</Select.Option>
        <Select.Option value={50}>50 / Page</Select.Option>
        <Select.Option value={100}>100 / Page</Select.Option>
      </Select>
    </div>
  );

  const handleViewDetails = (record) => {
    setState({
      selectedCalculation: record,
      detailsModalVisible: true,
    });
  };

  const handleRowSendForReview = async () => {
    const record = state.selectedCalculation;
    setState({ confirmActionModalVisible: false, selectedCalculation: null });
    const payoutId = record?.id;
    if (!payoutId) {
      message.error('Payout ID not found');
      return;
    }
    const response = await sendPayoutForReview(String(payoutId), activeViewType);
    if (response.data?.success) {
      message.success('Successfully sent for review');
      loadPayoutData();
    } else {
      message.error(
        response.errorMessage ||
        response.data?.failures[0]?.message ||
        'Failed to send for review',
      );
    }
  };

  const handleBulkSendForReview = async () => {
    const selectedPayoutIds = state.selectedRowKeys.map((key) => String(key));
    setState({ confirmActionModalVisible: false });

    const response = await bulkSendPayoutForReview(selectedPayoutIds, activeViewType);
    const successes = response.data?.data || [];
    const failures = response.data?.failures || [];

    const allResults = [
      ...successes.map((success) => ({
        payout_id: success.payout_id,
        entity_reference_number: success.entity_reference_number || '',
        message: success.message,
        isSuccess: true,
      })),
      ...failures.map((failure) => ({
        payout_id: failure.payout_id,
        entity_reference_number: failure.entity_reference_number,
        message: failure.message,
        isSuccess: false,
      })),
    ];

    GenericBulkUploadService.uploadData({
      heading: 'Sending Payouts for Review',
      data: allResults,
      batchFunction: async (item) => {
        if (item.isSuccess) {
          return {
            isSuccess: true,
          };
        }
        return {
          data: {
            failureArray: [
              {
                payout_id: item.payout_id,
                entity_reference_number: item.entity_reference_number,
                message: item.message,
              },
            ],
          },
        };
      },
      failureArrayKeysToShow: ['payout_id', 'entity_reference_number', 'message'],
      isSingleApi: true,
      onAllBatchesComplete: async () => {
        setState({ selectedRowKeys: [] });
        await loadPayoutData();
      },
    });
  };

  const handleDownloadAdhocChargeSample = async () => {
    try {
      const response = await downloadSampleAdhocChargesExcel();
      if (response?.isSuccess && response.data) {
        downloadFileData(response.data, 'adhoc_charges_sample', '.xlsx');
        message.success('Sample file downloaded successfully');
      } else {
        message.error(response.errorMessage || 'Failed to download sample');
      }
    } catch (error) {
      message.error('An error occurred while downloading sample');
    }
  };

  const handleParsedData = (dataList: any[], file: any) => {
    setState({ excelData: dataList });
  };

  const proceedWithAdhocChargeUpload = async (
    uploadData: any[],
    dataHeaders: string[],
    bypassValidation: boolean = false,
  ) => {
    try {
      const uploadPayload = bypassValidation
        ? { uploadData, bypassValidation: true }
        : { uploadData, validateOnly: false };

      const uploadResponse = await uploadBulkAdhocCharges(uploadPayload);
      if (!uploadResponse.isSuccess) {
        message.error(uploadResponse.errorMessage || 'Upload failed');
        return;
      }

      setState({
        validationPopupVisible: false,
        showBulkUploadAdhocChargeModal: false,
        excelData: [],
      });

      const totalBatches = uploadData.length;
      GenericBulkUploadService.uploadData({
        heading: 'Bulk Upload Adhoc Charges',
        data: uploadData.map((item, index) => ({
          ...item,
          batchNumber: index + 1,
          totalBatches,
        })),
        batchFunction: async (item) => {
          try {
            const response = await processSingleAdhocCharge(
              {
                uploadData: [item],
                dataHeaders,
                bulkUploadData: {
                  batchNumber: item.batchNumber,
                  totalBatches: item.totalBatches,
                },
              },
              activeViewType,
            );

            if (response.isSuccess && response.data?.success) {
              return { isSuccess: true };
            }

            if (response.isSuccess && response.data?.failures?.length > 0) {
              return {
                data: {
                  failureArray: response.data.failures,
                },
              };
            }
            return {
              data: {
                failureArray: [
                  {
                    rowNum: item.batchNumber,
                    message: response.errorMessage || response.data?.message || 'Processing failed',
                  },
                ],
              },
            };
          } catch (error) {
            return {
              data: {
                failureArray: [
                  {
                    rowNum: item.batchNumber,
                    message: error.message || 'An error occurred',
                  },
                ],
              },
            };
          }
        },
        failureArrayKeysToShow: ['rowNum', 'message'],
        isSingleApi: false,
        onAllBatchesComplete: async () => {
          await loadPayoutData();
        },
        dataHeaders,
      });
    } catch (error) {
      message.error('Failed to process bulk upload');
    }
  };

  const handleAdhocChargeExcelUpload = async (excelData: any[]) => {
    if (excelData?.length === 0) {
      message.error('Please upload a valid Excel file');
      return;
    }

    const currentFilters = getCurrentBucketFilters();

    const uploadData = excelData.map((row, index) => ({
      uniqueId: uuidv4(),
      entity_type: row['Entity type'] || row.entity_type,
      amount: parseFloat(row['Amount'] || row.amount),
      entity_reference_number: row['Entity reference number'] || row.entity_reference_number,
      charge_code: row['Charge code'] || row.charge_code,
      consignment_number: row['Consignment number'] || row.consignment_number || '',
      start_date: currentFilters.dateRange?.[0]?.format('YYYY-MM-DD'),
      end_date: currentFilters.dateRange?.[1]?.format('YYYY-MM-DD'),
      hub_id: currentFilters.hub,
    }));

    const dataHeaders = [
      'Entity type',
      'Entity reference number',
      'Charge code',
      'Consignment number',
      'Amount',
    ];

    try {
      const response = await uploadBulkAdhocCharges({
        uploadData,
        validateOnly: true,
      });

      setState({
        currentRequestBody: { uploadData, dataHeaders },
      });

      if (response.isSuccess && response.data?.success === false) {
        setState({
          duplicateData: {
            message: response.data?.message || 'Found a recent upload for adhoc charges.',
          },
          validationPopupVisible: true,
        });
        return;
      }

      if (response.isSuccess && response.data?.success === true) {
        proceedWithAdhocChargeUpload(uploadData, dataHeaders);
      } else if (response.data?.errors?.length) {
        setState({
          validationPopupVisible: true,
          validationErrors: response.data?.errors || [],
        });
      } else {
        message.error(response.errorMessage || 'Failed to validate Excel data');
      }
    } catch (error) {
      message.error('Failed to validate Excel data');
    }
  };

  const handleProceedWithUpload = async () => {
    if (state.currentRequestBody) {
      const { uploadData, dataHeaders } = state.currentRequestBody;
      setState({ duplicateData: null });
      await proceedWithAdhocChargeUpload(uploadData, dataHeaders, true);
    }
  };

  const getFieldValue = (item) => {
    if (item.type === 'payout' && !['payout', 'total_adhoc_charges', 'total_system_charges'].includes(item.column_id)) {
      return (_, record) => {
        const formattedValue = formatNumber(get(record, item.column_id, 0));
        if (item.show_info_icon) {
          return (
            <div>
              {formattedValue}
              <ReferenceDetailsPopover
                referenceData={{
                  payout: record.payout,
                  payout_type: record.payout_type,
                }}
              >
                <InfoCircleOutlined
                  className={classes.infoIcon}
                  onClick={(e) => e.stopPropagation()}
                />
              </ReferenceDetailsPopover>
            </div>
          );
        }

        return formattedValue;
      };
    }
    switch (item.column_id) {
      case 'event_date':
        return (_, record) => formatDateTime(record[item.column_id], 'DD MMM YYYY', '-');
      case 'last_updated_at':
      case 'pod_updated_at':
      case 'created_at':
        return (_, record) => formatDateTime(record[item.column_id], 'DD MMM YYYY, hh:mm A', '-');
      case 'approved_rejected_by':
        return (_, record) => {
          const employeeName = record.approved_rejected_by?.full_name || '-';
          const employeeCode = record.approved_rejected_by?.employee_code;
          return (
            <div className={classes.flexRowCenter}>
              <div>{employeeName}</div>
              {employeeCode && (
                <div className={classes.employeeCode}>
                  {employeeCode.toUpperCase()}
                </div>
              )}
            </div>
          );
        };
      case 'object_type':
      case 'payout_cycle':
        return (_, record) => {
          if (record.start_date && record.end_date) {
            return `${formatDateTime(record.start_date, 'DD MMM YYYY', '-')} - ${formatDateTime(
              record.end_date,
              'DD MMM YYYY',
              '-',
            )}`;
          }
          return '-';
        };
      case 'status':
        return (_, record) => {
          const status = get(record, 'status', 'open').toLowerCase();
          const statusConfig = STATUS_TYPES[status] || {
            backgroundColor: '#fff7e6',
            color: '#fa8c16',
          };
          const rejectionReason = get(record, 'remarks', '') || 'No reason provided';
          return (
            <div className={classes.flexRowCenter}>
              <div
                className={classes.statusBadge}
                style={{
                  backgroundColor: statusConfig.backgroundColor,
                  color: statusConfig.color,
                }}
              >
                {status}
              </div>
              {status === 'rejected' && (
                <Tooltip
                  title={
                    <div>
                      <div className={classes.tooltipTitle}>Rejection Reason</div>
                      <div>{rejectionReason}</div>
                    </div>
                  }
                  placement="top"
                >
                  <InfoCircleOutlined className={classes.rejectionInfoIcon} />
                </Tooltip>
              )}
            </div>
          );
        };
      case 'calculated_payout':
        return (_, record) => formatNumber(record.calculated_payout ?? record.final_payout);
      case 'final_payout':
      case 'computed_total_charges':
        return (_, record) => {
          return (
            <div className={classes.flexRowEndAlign}>
              <span>{formatNumber(record.computed_total_charges)}</span>
            </div>
          );
        };
      case 'actions':
        return (_, record) => {
          if (activeTabKey === 'pending') {
            const menu = (
              <Menu className={classes.actionsMenu}>
                <Menu.Item
                  key="add_charge"
                  onClick={() => {
                    setState({
                      selectedCalculation: record,
                      addChargeDrawerVisible: true,
                    });
                  }}
                  className={classes.menuItem}
                >
                  Add Charge
                </Menu.Item>
                <Menu.Item
                  key="send_for_review"
                  onClick={() => {
                    setState({
                      selectedCalculation: record,
                      confirmActionModalVisible: true,
                    });
                  }}
                  className={classes.menuItem}
                >
                  Send for Review
                </Menu.Item>
              </Menu>
            );

            return (
              <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                <Button
                  icon={<EllipsisOutlined style={{ fontSize: '14px' }} />}
                  className={classes.actionButton}
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            );
          }

          if (['approved', 'invoice_generated'].includes(activeTabKey)) {
            return (
              <Button
                onClick={() => handleViewDetails(record)}
                className={classes.viewButton}
              >
                View
              </Button>
            );
          }

          return (
            <Button
              onClick={() => handleViewDetails(record)}
              icon={<EllipsisOutlined style={{ fontSize: '14px' }} />}
              className={classes.actionButton}
            />
          );
        };
      case 'entity_reference_number':
        return (_, record) => (
          <div className={classes.flexColumn}>
            <span className={classes.entityReferenceLink}>
              {get(record, 'entity_reference_number', '-') || '-'}
            </span>
          </div>
        );
      case 'fixed_charges':
      case 'variable_charges':
        return (_, record) => (
          <span className={classes.textAlignRight}>
            {formatNumber(get(record, item.column_id, 0))}
          </span>
        );
      case 'payout':
      case 'total_adhoc_charges':
      case 'total_system_charges':
        return (_, record) => {
          let payoutArray = record.payout || [];
          if (item.column_id === 'total_adhoc_charges') {
            payoutArray = payoutArray.filter(
              (item) => item.payoutType === 'adhoc',
            );
          } else if (item.column_id === 'total_system_charges') {
            payoutArray = payoutArray.filter(
              (item) => item.payoutType !== 'adhoc',
            );
          }
          if (item.show_info_icon) {
            return (
              <div>
                {formatNumber(payoutArray.reduce(
                  (acc, curr) => acc + Number(curr.payout ?? 0),
                  0,
                ))}
                <ReferenceDetailsPopover
                  referenceData={{
                    payout: payoutArray,
                    payout_type: record.payout_type,
                    reference_number: record.reference_number,
                  }}
                >
                  <InfoCircleOutlined
                    className={classes.infoIcon}
                    onClick={(e) => e.stopPropagation()}
                  />
                </ReferenceDetailsPopover>
              </div>
            );
          }

          return formatNumber(payoutArray.reduce(
            (acc, curr) => acc + Number(curr.payout ?? 0),
            0,
          ));
        };
      case 'hub':
      case 'hub_name':
      case 'invoice_number':
        return (_, record) => get(record, item.column_id, '-') || '-';
      case 'creator':
        return (_, record) => {
          const creator = record.creator || {};
          const fullName = creator.full_name || '';
          const employeeCode = creator.employee_code || '';

          if (fullName) {
            return fullName;
          }
          if (employeeCode) {
            return employeeCode;
          }
          return '-';
        };
      case 'pod_updater':
      case 'last_updated_by':
        return (_, record) => (
          <div className={classes.updaterCell}>
            <div className={classes.updaterName}>
              {record[item.column_id]?.full_name || (!record[item.column_id]?.employee_code ? "-" : "")}
            </div>
            <div className={classes.updaterCode}>
              {record[item.column_id]?.employee_code}
            </div>
          </div>
        );
      case 'pod_status':
        return (_, record) => {
          const status = record.pod_status || 'pending';
          const statusConfig = STATUS_TYPES[status] || {
            backgroundColor: '#fff7e6',
            color: '#fa8c16',
          };

          return (
            <div
              className={classes.statusBadge}
              style={{
                backgroundColor: statusConfig.backgroundColor,
                color: statusConfig.color,
              }}
            >
              {status}
            </div>
          );
        };
      default:
        return (text, record) => {
          const value = get(record, item.column_id, text);
          let textColor = item.textColor || 'inherit';

          if (item.conditionalStyles && Array.isArray(item.conditionalStyles)) {
            const matchingStyle = item.conditionalStyles.find(
              (style) =>
                style.value === value ||
                (style.operator === '>' && value > style.value) ||
                (style.operator === '<' && value < style.value),
            );

            if (matchingStyle) {
              textColor = matchingStyle.textColor;
            }
          }

          // Convert object to string before rendering
          let displayValue;
          if (value === undefined || value === null) {
            displayValue = '-';
          } else if (typeof value === 'object') {
            displayValue = JSON.stringify(value);
          } else {
            displayValue = value;
          }

          if (displayValue?.length > 0 && displayValue !== '-') {
            displayValue = capitalize(displayValue);
          }
          return (
            <span
              style={{
                color: textColor,
                fontWeight: item.isBold ? 'bold' : 'normal',
                backgroundColor: item.background_color || 'transparent',
              }}
            >
              {displayValue}
            </span>
          );
        };
    }
  };

  const generateColumnsToShow = useCallback(() =>
    currentBucketColumns.map((item) => ({
      title: item.pretty_name,
      dataIndex: item.column_id,
      key: item.column_id,
      width: item.width || 150,
      fixed: item.fixed as 'left' | 'right' | undefined,
      align: item.align as 'left' | 'right' | 'center' | undefined,
      render: getFieldValue(item),
    })), [currentBucketColumns, activeTabKey]);

  const onBucketChange = (newTabKey: string) => {
    setState({ activeTabKey: newTabKey });
    if (!state.bucketFilters[newTabKey]) {
      setState({
        bucketFilters: {
          ...state.bucketFilters,
          [newTabKey]: getDefaultFiltersForBucket(),
        },
      });
    }
    setPagination((prev) => ({
      ...prev,
      pageNumber: 1,
      resultPerPage: 10,
    }));
  };

  const handleViewChange = (viewKey: string) => {
    if (viewKey === activeViewType) {
      return;
    }

    setState({ selectedRowKeys: [] });
    setPagination({
      ...pagination,
      pageNumber: 1,
      nextOrPrev: 'first',
      isNextPresent: false,
    });
    props.setActiveViewType(viewKey);
  };

  const renderViewSwitcher = () => {
    if (masterDataViews.length <= 1) {
      return null;
    }

    return (
      <div className={classes.viewSwitcher}>
        <Radio.Group
          value={activeViewType}
          onChange={(e) => handleViewChange(e.target.value)}
          disabled={tmsCarrierReceivables.loader}
        >
          {masterDataViews.map((view) => (
            <Radio.Button key={view.key} value={view.key}>
              {view.pretty_name || view.key}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>
    );
  };

  const renderFilters = () => (
    <Filters
      filters={getCurrentBucketFilters()}
      onChange={handleFiltersChange}
      classes={classes}
      activeTabKey={activeTabKey}
    />
  );

  const renderViewTable = () => (
    <div className={classes.tableWrapper}>
      <Table
        rowKey={(record: TmsCarrierReceivablesRecord) => record['id']}
        className={classes.table}
        dataSource={tmsCarrierReceivables.page_data || []}
        columns={generateColumnsToShow()}
        loading={tmsCarrierReceivables.loader}
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowSelection={
          activeTabKey === 'pending'
            ? {
              type: 'checkbox',
              onChange: (selectedRowKeys) => {
                setState({ selectedRowKeys });
              },
              selectedRowKeys: state.selectedRowKeys,
            }
            : undefined
        }
      />
    </div>
  );

  const renderTabsFromBuckets = () => (
    <div className={classes.tabsOuterWrapper}>
      <div className={classes.customTabsContainer}>
        {Object.entries(tmsCarrierReceivables?.payoutBuckets ?? {}).map(([key, value]) => {
          const isActive = activeTabKey === key;
          const isDisabled = tmsCarrierReceivables.loader;
          return (
            <div
              key={key}
              className={classNames(classes.bucketTab, { [classes.bucketTabActive]: isActive })}
              onClick={() => !isDisabled && onBucketChange(key)}
              role="tab"
              tabIndex={isDisabled ? -1 : 0}
              onKeyPress={(e) => !isDisabled && e.key === 'Enter' && onBucketChange(key)}
              style={isDisabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
            >
              <p
                className={classNames(classes.bucketTabText, {
                  [classes.bucketTabTextActive]: isActive,
                })}
              >
                {value}
              </p>
            </div>
          );
        })}
      </div>
      <div className={classes.tabContentContainer}>
        {renderViewSwitcher()}
        {renderFilters()}
        {renderActionBar()}
        {renderViewTable()}
      </div>
    </div>
  );

  return (
    <div className={classes.mainDiv}>
      {renderTabsFromBuckets()}
      <CalculationDetailsDrawer
        visible={detailsModalVisible}
        onClose={() => setState({ detailsModalVisible: false })}
        calculationData={selectedCalculation || {}}
      />
      <ConfirmActionModal
        visible={state.confirmActionModalVisible}
        onCancel={() => setState({
          confirmActionModalVisible: false,
          selectedCalculation: null,
        })}
        onConfirm={
          selectedCalculation
            ? handleRowSendForReview
            : handleBulkSendForReview
        }
        message={
          selectedCalculation
            ? 'You are about to submit '
            + `${capitalize(selectedCalculation.entity_type || '')} `
            + `${selectedCalculation.entity_reference_number || ''} `
            + 'for review'
            : `You are about to send ${state.selectedRowKeys.length} entities for review`
        }
      />
      <AddChargeDrawer
        visible={state.addChargeDrawerVisible}
        onClose={() => {
          setState({
            addChargeDrawerVisible: false,
            selectedCalculation: null,
          });
          loadPayoutData();
        }}
        selectedCalculation={state.selectedCalculation}
      />
      {/* Add Single Adhoc Charge Drawer */}
      <AddChargeDrawer
        visible={state.showAddAdhocChargeModal}
        onClose={() => setState({ showAddAdhocChargeModal: false })}
        onSuccess={() => {
          loadPayoutData();
        }}
      />
      {/* Bulk Upload Adhoc Charges Modal */}
      <Modal
        title="Bulk Upload Adhoc Charges"
        visible={state.showBulkUploadAdhocChargeModal}
        onCancel={() => setState({ showBulkUploadAdhocChargeModal: false, excelData: [] })}
        onOk={() => {
          handleAdhocChargeExcelUpload(state.excelData);
        }}
        okText="Submit"
        okButtonProps={{
          disabled: !state.excelData?.length,
        }}
        destroyOnClose
      >
        <ExcelUpload
          onDataParsed={handleParsedData}
          sampleApi={handleDownloadAdhocChargeSample}
          onRemove={() => setState({ excelData: [] })}
        />
        {Array.isArray(state.excelData) && state.excelData.length > 0 && (
          <div className={classes.bulkUploadInfoBox}>
            {(() => {
              const currentFilters = getCurrentBucketFilters();
              const startDate = currentFilters.dateRange?.[0]
                ? currentFilters.dateRange[0].format('DD MMM YYYY')
                : '';
              const endDate = currentFilters.dateRange?.[1]
                ? currentFilters.dateRange[1].format('DD MMM YYYY')
                : '';
              const hubName = currentFilters.hubName || '';
              return `Uploading adhoc charges for billing cycle ${startDate} - ${endDate} and hub ${hubName}. Total rows: ${state.excelData.length}`;
            })()}
          </div>
        )}
      </Modal>
      {/* Duplicate Validation Modal */}
      {state.duplicateData && (
        <Modal
          title="Proceed"
          visible={state.validationPopupVisible}
          onCancel={() => setState({ validationPopupVisible: false, duplicateData: null })}
          footer={[
            <Button
              key="cancel"
              onClick={() => setState({ validationPopupVisible: false, duplicateData: null })}
            >
              {t('Cancel')}
            </Button>,
            <Button key="proceed" type="primary" onClick={handleProceedWithUpload}>
              {t('Proceed Anyway')}
            </Button>,
          ]}
        >
          <p>{state.duplicateData.message}</p>
          <p>{t('Do you want to proceed with the upload anyway?')}</p>
        </Modal>
      )}
      {/* Validation Errors Modal */}
      {!state.duplicateData && state.validationErrors.length > 0 && (
        <Modal
          title={
            <div className={classes.validationModalHeader}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
              <span>{t('Validation Errors')}</span>
            </div>
          }
          visible={state.validationPopupVisible}
          onCancel={() => setState({ validationPopupVisible: false, validationErrors: [] })}
          width={700}
          footer={[
            <Button
              key="cancel"
              onClick={() => setState({ validationPopupVisible: false, validationErrors: [] })}
            >
              {t('Cancel')}
            </Button>,
            <Button
              key="bypass"
              type="primary"
              danger
              onClick={() => {
                if (state.currentRequestBody) {
                  const { uploadData, dataHeaders } = state.currentRequestBody;
                  proceedWithAdhocChargeUpload(uploadData, dataHeaders, true);
                }
              }}
            >
              {t('Bypass Validation')}
            </Button>,
          ]}
        >
          <div className={classes.validationDescription}>
            {t(
              'The following errors were found in the uploaded file. You can fix these errors and upload again, or bypass validation to proceed anyway.',
            )}
          </div>
          <Table
            columns={[
              {
                title: t('Row Number'),
                dataIndex: 'rowNum',
                key: 'rowNum',
                width: '15%',
              },
              {
                title: t('Error Message'),
                dataIndex: 'message',
                key: 'message',
                width: '85%',
                render: (text: string) => text || 'Unknown error',
              },
            ]}
            dataSource={state.validationErrors.map((error, index) => ({
              ...error,
              key: index,
            }))}
            pagination={false}
            scroll={{ y: 400 }}
            size="small"
          />
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  payoutBuckets: state.payoutBuckets,
  tmsCarrierReceivables: state.receivables,
});

const mapDispatchToProps = (dispatch) => ({
  setFilters: (data) =>
    dispatch({
      type: SET_RECEIVABLES_FILTERS,
      data,
    }),
  setPagination: (data) =>
    dispatch({
      type: SET_RECEIVABLES_PAGINATION,
      data,
    }),
  fetchPayoutData: (data) => dispatch(fetchPayoutData(data)),
  setActiveViewType: (viewKey) => dispatch(setActiveViewType(viewKey)),
});

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: TmsCarrierReceivablesStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(Calculations);
